/**
 * <ArticleReaderToolbar />
 *
 * Sticky per-article toolbar with:
 *   • Translate — 20 popular languages inline, "More…" opens full picker.
 *     Uses /api/i18n/translate (Emergent LLM). Original HTML is preserved
 *     and can be restored with one tap.
 *   • Listen (TTS) — browser speechSynthesis Play / Pause / Stop with
 *     progress. Reads the currently-visible language (original OR
 *     translated). Voice auto-selected to match the language.
 *
 * Contract:
 *   <ArticleReaderToolbar
 *     bodyRef={contentRef}          // ref to the <div> containing article body
 *     title={article.title}         // optional — read first
 *     source_lang="en"              // default source language
 *     storageKey={`article:${slug}`}// remembers language pref per article
 *     testid="article-reader-tools" // dev automation hook
 *   />
 *
 * The component NEVER mutates the DOM outside `bodyRef.current`. When the
 * user reverts, it restores the exact original innerHTML captured on first
 * translation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Languages, Volume2, Pause, StopCircle, ChevronDown, Loader2, RotateCcw } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { getPreferredLang } from "@/components/LanguageChip";

const POPULAR_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "ml", name: "Malayalam" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "kn", name: "Kannada" },
  { code: "bn", name: "Bengali" },
  { code: "mr", name: "Marathi" },
  { code: "gu", name: "Gujarati" },
  { code: "pa", name: "Punjabi" },
  { code: "ur", name: "Urdu" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "pt", name: "Portuguese" },
  { code: "ar", name: "Arabic" },
  { code: "ru", name: "Russian" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
];

// Split large text into safe chunks for the TTS engine — most browsers
// truncate silently past ~200 chars.
function chunkText(text, size = 180) {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (!clean) return [];
  const sentences = clean.split(/(?<=[\.\!\?।؟。！？])\s+/);
  const out = [];
  let buf = "";
  for (const s of sentences) {
    if ((buf + " " + s).trim().length > size && buf) {
      out.push(buf.trim());
      buf = s;
    } else {
      buf = (buf ? buf + " " : "") + s;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

function pickVoiceFor(lang) {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices || !voices.length) return null;
  const match =
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(lang.toLowerCase())) ||
    voices.find((v) => v.lang && v.lang.toLowerCase().startsWith("en"));
  return match || null;
}

// pull rendered text out of an HTML container, honouring headings/lists.
function extractReadableText(root, includeTitle) {
  if (!root) return "";
  const clone = root.cloneNode(true);
  clone.querySelectorAll("script,style,noscript,iframe,button,form").forEach((n) => n.remove());
  const parts = includeTitle ? [includeTitle] : [];
  parts.push(clone.textContent || "");
  return parts.join(". ").replace(/\s+/g, " ").trim();
}

export default function ArticleReaderToolbar({
  bodyRef,
  title = "",
  source_lang = "en",
  storageKey,
  testid = "article-reader-tools",
}) {
  const [openLangs, setOpenLangs] = useState(false);
  const [targetLang, setTargetLang] = useState(source_lang);
  const [translating, setTranslating] = useState(false);
  const [ttsState, setTtsState] = useState("idle"); // idle | playing | paused
  const [ttsProgress, setTtsProgress] = useState({ current: 0, total: 0 });

  const originalHtmlRef = useRef(null);
  const utterancesRef = useRef([]);
  const canceledRef = useRef(false);

  // Restore pref
  useEffect(() => {
    try {
      const s = storageKey && localStorage.getItem(`reader:lang:${storageKey}`);
      if (s) setTargetLang(s);
      else {
        const bl = (getPreferredLang && getPreferredLang()) || navigator?.language?.slice(0, 2) || "en";
        if (bl && bl !== source_lang) setTargetLang(bl);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [storageKey, source_lang]);

  const stopTts = useCallback(() => {
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
    canceledRef.current = true;
    utterancesRef.current = [];
    setTtsState("idle");
    setTtsProgress({ current: 0, total: 0 });
  }, []);

  // Clean up TTS on unmount
  useEffect(() => () => stopTts(), [stopTts]);

  const revert = useCallback(() => {
    if (!bodyRef?.current || originalHtmlRef.current == null) return;
    bodyRef.current.innerHTML = originalHtmlRef.current;
    setTargetLang(source_lang);
    try { storageKey && localStorage.removeItem(`reader:lang:${storageKey}`); } catch { /* ignore */ }
  }, [bodyRef, source_lang, storageKey]);

  const translate = useCallback(async (code) => {
    if (!bodyRef?.current) return;
    if (code === source_lang) { revert(); return; }
    setOpenLangs(false);
    if (translating) return;
    setTranslating(true);
    // Stop any playback while we swap languages
    stopTts();
    try {
      if (originalHtmlRef.current == null) originalHtmlRef.current = bodyRef.current.innerHTML;

      // Build a plain-text extract; translate in chunks of up to 6 kB to
      // respect model context and give the LLM a fighting chance to keep
      // paragraph breaks.
      const raw = bodyRef.current.innerText || bodyRef.current.textContent || "";
      const paragraphs = raw.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
      const grouped = [];
      let buf = "";
      for (const p of paragraphs) {
        if ((buf + "\n\n" + p).length > 5500 && buf) {
          grouped.push(buf);
          buf = p;
        } else {
          buf = (buf ? buf + "\n\n" : "") + p;
        }
      }
      if (buf) grouped.push(buf);

      const results = [];
      for (const group of grouped) {
        const { data } = await http.post("/i18n/translate", {
          text: group,
          target_lang: code,
          source_lang,
        });
        results.push(data.translated || group);
      }
      const translatedText = results.join("\n\n");

      // Render the translation as reasonably-styled HTML — keep paragraph
      // breaks, escape tags to prevent stored-HTML surprises.
      const html = translatedText
        .split(/\n{2,}/)
        .map((p) =>
          `<p>${p
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")}</p>`
        )
        .join("\n");
      bodyRef.current.innerHTML = html;
      bodyRef.current.setAttribute("dir", "auto");
      bodyRef.current.setAttribute("lang", code);

      setTargetLang(code);
      try { storageKey && localStorage.setItem(`reader:lang:${storageKey}`, code); } catch { /* ignore */ }
      toast.success(`Translated to ${POPULAR_LANGUAGES.find((l) => l.code === code)?.name || code}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Translation failed. Please try again.";
      toast.error(msg);
    } finally {
      setTranslating(false);
    }
  }, [bodyRef, source_lang, storageKey, translating, stopTts, revert]);

  const play = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      toast.error("Your browser doesn't support text-to-speech.");
      return;
    }
    if (ttsState === "paused") {
      window.speechSynthesis.resume();
      setTtsState("playing");
      return;
    }
    if (ttsState === "playing") {
      window.speechSynthesis.pause();
      setTtsState("paused");
      return;
    }
    const text = extractReadableText(bodyRef?.current, title);
    if (!text) { toast.error("Nothing to read on this page."); return; }
    const chunks = chunkText(text);
    if (!chunks.length) return;
    canceledRef.current = false;
    setTtsProgress({ current: 0, total: chunks.length });
    setTtsState("playing");
    const voice = pickVoiceFor(targetLang);
    let index = 0;
    const speakNext = () => {
      if (canceledRef.current) return;
      if (index >= chunks.length) {
        setTtsState("idle");
        setTtsProgress({ current: chunks.length, total: chunks.length });
        return;
      }
      const u = new window.SpeechSynthesisUtterance(chunks[index]);
      u.lang = targetLang;
      if (voice) u.voice = voice;
      u.rate = 0.96;
      u.pitch = 1.0;
      u.onend = () => {
        index += 1;
        setTtsProgress({ current: Math.min(index, chunks.length), total: chunks.length });
        speakNext();
      };
      u.onerror = () => {
        index += 1;
        speakNext();
      };
      utterancesRef.current.push(u);
      try { window.speechSynthesis.speak(u); } catch { /* ignore */ }
    };
    speakNext();
  }, [bodyRef, title, targetLang, ttsState]);

  const currentLangLabel = useMemo(() => {
    const l = POPULAR_LANGUAGES.find((x) => x.code === targetLang);
    return l?.name || targetLang.toUpperCase();
  }, [targetLang]);

  const showRevert = originalHtmlRef.current != null && targetLang !== source_lang;

  return (
    <div
      data-testid={testid}
      className="sticky top-2 z-30 -mx-2 md:mx-0 mb-6 px-3 md:px-4 py-2.5 rounded-2xl flex items-center gap-2 flex-wrap"
      style={{
        background: "rgba(255, 247, 237, 0.85)",
        border: "1px solid rgba(194, 65, 12, 0.18)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "0 8px 24px -12px rgba(194, 65, 12, 0.15)",
      }}
    >
      {/* Translate cluster */}
      <div className="relative">
        <button
          data-testid={`${testid}-translate-btn`}
          onClick={() => setOpenLangs((v) => !v)}
          disabled={translating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
          style={{ background: "#C2410C", color: "#fff" }}
          aria-haspopup="listbox"
          aria-expanded={openLangs}
        >
          {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
          <span>Translate</span>
          <span className="opacity-90 hidden sm:inline">· {currentLangLabel}</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {openLangs && (
          <div
            data-testid={`${testid}-lang-menu`}
            role="listbox"
            className="absolute top-full mt-2 left-0 min-w-[240px] max-h-[320px] overflow-auto rounded-xl shadow-lg z-40"
            style={{ background: "#fff", border: "1px solid rgba(194,65,12,0.15)" }}
          >
            {POPULAR_LANGUAGES.map((l) => (
              <button
                key={l.code}
                data-testid={`${testid}-lang-${l.code}`}
                onClick={() => translate(l.code)}
                className="w-full text-left px-3.5 py-2 text-sm hover:bg-orange-50 flex items-center justify-between"
                style={{ color: "#431a04" }}
                role="option"
                aria-selected={targetLang === l.code}
              >
                <span>{l.name}</span>
                {targetLang === l.code && (
                  <span className="text-xs font-semibold" style={{ color: "#C2410C" }}>Active</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {showRevert && (
        <button
          data-testid={`${testid}-revert-btn`}
          onClick={revert}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium"
          style={{ background: "transparent", color: "#6b3410", border: "1px solid rgba(107,52,16,0.25)" }}
        >
          <RotateCcw className="w-3.5 h-3.5" /> Original
        </button>
      )}

      {/* Listen (TTS) */}
      <div className="flex items-center gap-2 ml-auto">
        <button
          data-testid={`${testid}-listen-btn`}
          onClick={play}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
          style={{
            background: ttsState === "idle" ? "#fff" : "#431a04",
            color: ttsState === "idle" ? "#431a04" : "#fff",
            border: "1px solid rgba(67, 26, 4, 0.2)",
          }}
          aria-label={ttsState === "idle" ? "Listen" : ttsState === "playing" ? "Pause" : "Resume"}
        >
          {ttsState === "playing" ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          <span>{ttsState === "idle" ? "Listen" : ttsState === "playing" ? "Pause" : "Resume"}</span>
        </button>
        {ttsState !== "idle" && (
          <button
            data-testid={`${testid}-stop-btn`}
            onClick={stopTts}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-medium"
            style={{ background: "transparent", color: "#6b3410", border: "1px solid rgba(107,52,16,0.25)" }}
            aria-label="Stop"
          >
            <StopCircle className="w-3.5 h-3.5" /> Stop
          </button>
        )}
        {ttsState !== "idle" && ttsProgress.total > 0 && (
          <span className="text-[11px] tabular-nums" style={{ color: "#6b3410" }}>
            {ttsProgress.current}/{ttsProgress.total}
          </span>
        )}
      </div>
    </div>
  );
}
